/**
 * Hook for synchronizing menu scope state with navigation state
 * Ensures selectedScope (localStorage) and typeNavigation stay in sync
 * Resolves desynchronization issues between TopBar and localStorage
 *
 * @module hooks/menuScope/useMenuScopeSync
 *
 * @returns {void} This hook only handles synchronization side effects
 *
 * @example
 * // Use in TopNavigationBarStores to ensure sync
 * useMenuScopeSync();
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { NavigationType } from '@experimental-components/TopNavigationBar/types';
import { MENU_SCOPE } from 'utils/menuMaker/constants';
import useNavigation from 'hooks/navigation/useNavigation';
import { isMenuPath } from 'components/TopNavigationBarStores/utils';

import { useMenuScopeManagement } from './useMenuScopeManagement';

/**
 * Hook that synchronizes menu scope state with navigation state
 * Uses a conservative approach to prevent infinite loops while maintaining sync
 *
 * Logic:
 * - Only syncs when there's a clear mismatch that needs correction
 * - Avoids aggressive syncing that can cause loops
 * - Prioritizes navigation type as source of truth for consistency
 */
export const useMenuScopeSync = (): void => {
  const { selectedScope, setMenuScope, clearMenuScope } = useMenuScopeManagement();
  const { currentNavigation: typeNavigation, setNavigation } = useNavigation();
  const location = useLocation();

  // Conservative sync: Only update when there's a clear mismatch
  // Prioritize navigation type as the source of truth to reduce conflicts
  useEffect(() => {
    if (!isMenuPath(location.pathname)) {
      return;
    }

    // If navigation is STORE_DRAFT but scope is not ORGANIZATION → Set ORGANIZATION scope
    if (typeNavigation === NavigationType.STORE_DRAFT && selectedScope !== MENU_SCOPE.ORGANIZATION) {
      setMenuScope(MENU_SCOPE.ORGANIZATION);
    }
    // If navigation is ORGANIZATION but scope is not null → Clear scope
    else if (typeNavigation === NavigationType.ORGANIZATION && selectedScope !== null) {
      clearMenuScope();
    }
    // If user explicitly selected ORGANIZATION scope but navigation isn't STORE_DRAFT → Update navigation
    else if (selectedScope === MENU_SCOPE.ORGANIZATION && typeNavigation !== NavigationType.STORE_DRAFT) {
      setNavigation(NavigationType.STORE_DRAFT);
    }
    // If scope is cleared but still in draft mode → Reset to store navigation
    else if (selectedScope === null && typeNavigation === NavigationType.STORE_DRAFT) {
      setNavigation(NavigationType.STORE);
    }
  }, [selectedScope, typeNavigation, setMenuScope, clearMenuScope, setNavigation]);
};
